import { useSaveCommonCriteria } from '@src/entities/Criteria/ui/hooks/useSaveCommonCriteria';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Modal as ModalComponent } from '@uhg-abyss/web/ui/Modal';
import { isEqual, isNull } from 'lodash';
import React from 'react';

/**
 * Modal: Activate
 *
 * This modal is used to activate the selected version of the entity.
 *
 * @param activatedVersion
 * @param commonCriteriaVersions
 * @param currentEntity
 * @param formValues
 * @param isOpen
 * @param refetch
 * @param selectedVersionValue
 * @param setFocusedEntity
 * @param setIsOpen
 * @returns {Element}
 * @constructor
 */
export function Modal({
  activatedVersion = {},
  commonCriteriaVersions = 0,
  currentEntity = {},
  formValues = {},
  isOpen = false,
  refetch = () => {},
  selectedVersionValue = 0,
  setFocusedEntity = () => {},
  setIsOpen = () => {},
}) {
  const handleActivation = useSaveCommonCriteria('activate');
  const handleSave = useSaveCommonCriteria('update');
  const handleCreateVersion = useSaveCommonCriteria('createVersion');

  /**
   * handleClose
   *
   * Handles the closing of the modal.
   *
   * @returns {Promise<void>}
   */
  const handleClose = () => {
    setIsOpen(false);
  };

  const inactiveToActive =
    isEqual(currentEntity?.activeCommonCriteriaVersion?.criteria, formValues?.filters) ||
    (isNull(currentEntity?.activeCommonCriteriaVersion) && currentEntity?.activeVersionNbr === -1);
  const versionChange = activatedVersion?.version !== selectedVersionValue;
  let content;

  if (inactiveToActive) {
    content = (
      <div>
        Are you sure you want to activate: <strong>{currentEntity?.name}</strong>
      </div>
    );
  } else if (versionChange) {
    content = (
      <React.Fragment>
        <div>
          Do you want to activate{' '}
          <strong>
            {currentEntity?.name} v{selectedVersionValue}
          </strong>
          ?
        </div>
        <div style={{ marginTop: '4px' }}>
          This will deactivate v{activatedVersion.version} and make v{selectedVersionValue} the current active version.
        </div>
      </React.Fragment>
    );
  } else {
    content = (
      <div>
        You made updates to <strong>{currentEntity?.name}</strong>. Do you want to activate a new version v
        {commonCriteriaVersions}{' '}
      </div>
    );
  }

  return (
    <ErrorHandler location="src/features/CommonCriteria/Activate/components/Modal/Modal.jsx">
      <ModalComponent isOpen={isOpen} onClose={handleClose} title="Activate Common Criteria">
        <ModalComponent.Section>{content}</ModalComponent.Section>
        <ModalComponent.Section>
          <Divider height={1} />
          <Layout.Group alignLayout="right">
            <Button onClick={handleClose} variant="outline">
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (inactiveToActive) {
                  await handleActivation(
                    {
                      ...currentEntity,
                      ...formValues,
                      ...{
                        activeVersionNbr: formValues?.version,
                        isActive: true,
                      },
                    },
                    refetch
                  );
                } else if (versionChange) {
                  await handleSave({ ...currentEntity, ...formValues }, refetch);
                } else {
                  await handleCreateVersion({ ...currentEntity, ...formValues }, refetch);
                }
                setFocusedEntity(currentEntity?.name);
              }}
              variant="solid"
            >
              Activate
            </Button>
          </Layout.Group>
        </ModalComponent.Section>
      </ModalComponent>
    </ErrorHandler>
  );
}
